import { ApolloError, useMutation } from "@apollo/client";
import {
  UpdateRetailWorkspaceNameDoc,
  UpdateWorkspaceNameDoc,
} from "operations";
import { ErrorReport } from "lib/Datadog";
import { extractErrorReportFromApolloError } from "lib/Apollo";
import { useNotifications } from "@joor/design-system";
import { useGlobalAssortmentId } from "components/Routes";

type ErrorFunction = (errors: ErrorReport) => void;
type SuccessFunction = (name: string) => void;

const useToasts = (type: string): [ErrorFunction, SuccessFunction] => {
  const { addToasts } = useNotifications();
  return [
    (errors: ErrorReport) => {
      addToasts([
        {
          title: `Failed To Create ${type}`,
          description: `${errors.message}`,
          type: "error",
        },
      ]);
    },
    (name: string) => {
      addToasts([
        {
          title: `${type} Name Updated`,
          description: `${type} name was successfully updated to '${name}'.`,
          type: "success",
        },
      ]);
    },
  ];
};

export const useUpdateWorkspaceName = (workspaceId: string) => {
  const [mutate] = useMutation(UpdateRetailWorkspaceNameDoc);
  const [displayErrorToast, displaySuccessToast] = useToasts("Workspace");

  return async (name: string) => {
    const trimmedName = name.trim();
    try {
      const { data, errors } = await mutate({
        variables: { workspaceId, name: trimmedName },
        optimisticResponse: {
          updateRetailWorkspaceName: {
            retailWorkspace: {
              id: workspaceId,
              name: trimmedName,
              __typename: "RetailWorkspace",
            },
          },
        },
      });
      if (errors) {
        displayErrorToast(
          extractErrorReportFromApolloError({
            graphQLErrors: errors,
          } as ApolloError)
        );
      } else {
        displaySuccessToast(
          data?.updateRetailWorkspaceName.retailWorkspace?.name || ""
        );
      }
    } catch (e) {
      displayErrorToast(extractErrorReportFromApolloError(e as ApolloError));
    }
  };
};

export const useUpdateOrderName = () => {
  const assortmentId = useGlobalAssortmentId();
  const [mutate] = useMutation(UpdateWorkspaceNameDoc);
  const [displayErrorToast, displaySuccessToast] = useToasts("Order");
  return async (name: string) => {
    const trimmedName = name.trim();
    try {
      const { data, errors } = await mutate({
        variables: { assortmentId, name: trimmedName },
        optimisticResponse: {
          updateAssortmentDetails: {
            assortment: {
              id: assortmentId,
              name: trimmedName,
            },
          },
        },
      });
      if (errors) {
        displayErrorToast(
          extractErrorReportFromApolloError({
            graphQLErrors: errors,
          } as ApolloError)
        );
      } else {
        displaySuccessToast(
          data?.updateAssortmentDetails.assortment?.name || ""
        );
      }
    } catch (e) {
      displayErrorToast(extractErrorReportFromApolloError(e as ApolloError));
    }
  };
};
